import { useState, useEffect, useCallback } from 'react';
import { Plus, X, Loader, CheckCircle, Clock, AlertTriangle, Trash2 } from 'lucide-react';
import api from '../services/api';

const STATUS_STYLES = {
  pending: 'bg-orange-100 text-orange-700',
  noted:   'bg-green-100 text-green-700',
};

const STATUS_LABELS = { pending: 'Pending', noted: 'Noted' };

const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const d = new Date(2000, i, 1);
  return { value: String(i + 1).padStart(2, '0'), label: d.toLocaleString('default', { month: 'long' }) };
});

function currentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export default function AdvanceActivations() {
  const [records, setRecords]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('pending');
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast]         = useState(null);
  const [deleting, setDeleting]   = useState(null);
  const [toggling, setToggling]   = useState(null);

  // form state
  const [form, setForm] = useState({ username: '', advance_month: currentYearMonth(), notes: '' });
  const [saving, setSaving] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const res = await api.get('/advance-activations/', { params });
      // DRF may return either a plain list or a paginated `{results: []}`
      // object depending on the deployment's pagination settings.
      const payload = res.data;
      setRecords(Array.isArray(payload) ? payload : (Array.isArray(payload?.results) ? payload.results : []));
    } catch {
      showToast('Failed to load records', 'error');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  // Load records whenever the selected status changes.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.advance_month) return;
    setSaving(true);
    try {
      await api.post('/advance-activations/', form);
      showToast('Advance activation logged');
      setShowModal(false);
      setForm({ username: '', advance_month: currentYearMonth(), notes: '' });
      load();
    } catch (err) {
      const msg = err.response?.data?.username?.[0] || err.response?.data?.detail || 'Failed to save';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (rec) => {
    const newStatus = rec.status === 'pending' ? 'noted' : 'pending';
    setToggling(rec.id);
    try {
      const res = await api.patch(`/advance-activations/${rec.id}/`, { status: newStatus });
      setRecords(prev => prev.map(r => r.id === rec.id ? res.data : r));
    } catch {
      showToast('Failed to update status', 'error');
    } finally {
      setToggling(null);
    }
  };

  const deleteRecord = async (id) => {
    if (!window.confirm('Remove this advance activation record?')) return;
    setDeleting(id);
    try {
      await api.delete(`/advance-activations/${id}/`);
      setRecords(prev => prev.filter(r => r.id !== id));
      showToast('Record removed');
    } catch {
      showToast('Failed to delete', 'error');
    } finally {
      setDeleting(null);
    }
  };

  const formatMonth = (ym) => {
    if (!ym) return '—';
    const [y, m] = ym.split('-');
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-semibold ${
          toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
        }`}>
          {toast.type === 'error' ? <X size={15} /> : <CheckCircle size={15} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <span className="w-1 h-6 bg-orange-500 rounded-full" />
            Advance Activations
          </h2>
          <p className="text-sm text-gray-500 mt-0.5 ml-3">
            Track customers whose packages were accidentally activated one month ahead.
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-xl transition-colors"
        >
          <Plus size={15} /> Log Entry
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-5">
        {['pending', 'noted', 'all'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`pb-3 px-4 text-sm font-medium border-b-2 -mb-px capitalize transition-colors ${
              filter === f ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {f === 'all' ? 'All' : STATUS_LABELS[f]}
            {f !== 'all' && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full font-bold ${
                filter === f ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'
              }`}>
                {records.filter(r => f === 'all' || r.status === f).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader size={22} className="animate-spin text-gray-400" />
          </div>
        ) : records.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <AlertTriangle size={30} className="mb-2 opacity-40" />
            <p className="text-sm">No advance activations recorded.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
                <tr>
                  <th className="px-5 py-3 text-left">Customer</th>
                  <th className="px-5 py-3 text-left">Advance Month</th>
                  <th className="px-5 py-3 text-left hidden sm:table-cell">Package / Area</th>
                  <th className="px-5 py-3 text-left">Notes</th>
                  <th className="px-5 py-3 text-left hidden md:table-cell">Logged By</th>
                  <th className="px-5 py-3 text-left">Status</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {records.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3">
                      <p className="font-semibold text-gray-800">{r.username}</p>
                      {r.customer_name && <p className="text-xs text-gray-400">{r.customer_name}</p>}
                    </td>
                    <td className="px-5 py-3 font-medium text-orange-700">
                      {formatMonth(r.advance_month)}
                    </td>
                    <td className="px-5 py-3 hidden sm:table-cell text-gray-500 text-xs">
                      <p>{r.customer_package || '—'}</p>
                      <p>{r.customer_area || '—'}</p>
                    </td>
                    <td className="px-5 py-3 text-gray-500 max-w-[180px]">
                      <p className="truncate">{r.notes || '—'}</p>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell text-gray-500 text-xs">
                      <p>{r.logged_by_username || '—'}</p>
                      <p>{r.logged_at ? new Date(r.logged_at).toLocaleDateString() : ''}</p>
                    </td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => toggleStatus(r)}
                        disabled={toggling === r.id}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${STATUS_STYLES[r.status]} hover:opacity-80`}
                      >
                        {toggling === r.id
                          ? <Loader size={11} className="animate-spin" />
                          : r.status === 'pending' ? <Clock size={11} /> : <CheckCircle size={11} />
                        }
                        {STATUS_LABELS[r.status]}
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => deleteRecord(r.id)}
                        disabled={deleting === r.id}
                        className="text-gray-300 hover:text-red-500 transition-colors"
                      >
                        {deleting === r.id
                          ? <Loader size={14} className="animate-spin" />
                          : <Trash2 size={14} />
                        }
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-bold text-gray-800">Log Advance Activation</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Customer Username *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 7city167"
                  value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Advance Month *</label>
                <div className="flex gap-2">
                  <select
                    value={form.advance_month.split('-')[0]}
                    onChange={e => setForm(f => ({ ...f, advance_month: `${e.target.value}-${f.advance_month.split('-')[1]}` }))}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                  >
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <select
                    value={form.advance_month.split('-')[1]}
                    onChange={e => setForm(f => ({ ...f, advance_month: `${f.advance_month.split('-')[0]}-${e.target.value}` }))}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                  >
                    {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <p className="text-xs text-gray-400 mt-1">The month that was accidentally pre-activated</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
                <textarea
                  rows={3}
                  placeholder="Who activated it, what happened..."
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none resize-none"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                >
                  {saving ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
                  {saving ? 'Saving...' : 'Log Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
